import React from 'react';
import { Box, Button, Flex, Heading, SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { AxiosError } from 'axios';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ArrowsLeftRight,
  Bank,
  Buildings,
  CaretRight,
  ChartLineUp,
  Coins,
  CurrencyDollar,
  FileText,
  Gauge,
  Receipt,
  Scales,
  ShoppingBag,
  Sparkle,
  TrendDown,
  TrendUp,
  Trophy,
  UsersThree,
  MagnifyingGlass,
  WarningCircle,
} from 'phosphor-react';

import { GlassCard, GlassPageBg } from '../../../components/glass';
import {
  IFinanceiroBaseParams,
  IFinanceiroDashboard,
  IFinanceiroDre,
  IFinanceiroFluxo,
  useAtualizarFinanceiroInsights,
  useFinanceiroDashboard,
  useFinanceiroDre,
  useFinanceiroFluxo,
  useFinanceiroInsights,
  useFinanceiroKpis,
} from '../../../hooks/useFinanceiro';
import { fmtDataHora, useCooldownAtivo } from '../../../hooks/useCooldownAtivo';
import { IPrivateRoutes } from '../../../routes/private.routes';
import { usePermittedSubmodules } from '../../../utils/getSubmodulesForPath';
import { isContaInterna } from './components/contaTipo';
import { brl } from './components/format';
import { InfoPopover } from './components/InfoPopover';
import { KpiCard } from './components/KpiCard';
import { brlCompact, fmtPct, pct } from './components/theme';
import { useFinanceiroFilters } from './components/useFinanceiroFilters';

const SEV: Record<string, string> = {
  critico: '#DC2626',
  atencao: '#D97706',
  info: '#1B88F5',
  bom: '#16A34A',
};

// Δ% vs período anterior. upGood = subir é positivo para esta métrica.
const mkDelta = (cur: number, prev: number, upGood: boolean) =>
  prev > 0 ? { pct: ((cur - prev) / prev) * 100, good: cur >= prev === upGood } : undefined;

/** Carimbo "Atualizado em ..." + botão Atualizar dos insights (snapshot no
 *  servidor; cooldown de 5h por combinação de filtros — o gate é do backend,
 *  aqui só refletimos o estado e mostramos o 429 amigável). */
function InsightsAtualizar({
  params,
  geradoEm,
  liberadaEm,
}: {
  params: IFinanceiroBaseParams;
  geradoEm?: string | null;
  liberadaEm?: string | null;
}) {
  const toast = useToast();
  const atualizar = useAtualizarFinanceiroInsights();
  const bloqueado = useCooldownAtivo(liberadaEm);
  return (
    <Box textAlign="end" flexShrink={0} ms="auto" position="relative">
      {geradoEm && (
        <Text fontSize="11px" color="#94A3B8" whiteSpace="nowrap">
          Atualizado em {fmtDataHora(geradoEm)}
        </Text>
      )}
      <Button
        size="xs"
        variant="ghost"
        colorScheme="purple"
        isLoading={atualizar.isPending}
        isDisabled={bloqueado}
        title={bloqueado ? `Nova atualização disponível às ${fmtDataHora(liberadaEm)}` : undefined}
        onClick={() =>
          atualizar.mutate(params, {
            onError: err => {
              const ax = err as AxiosError<{ detail?: string }>;
              toast({
                status: ax.response?.status === 429 ? 'warning' : 'error',
                description: ax.response?.data?.detail ?? 'Não foi possível atualizar os insights.',
                duration: 6000,
                isClosable: true,
              });
            },
          })
        }
      >
        {bloqueado ? `Disponível às ${fmtDataHora(liberadaEm)}` : 'Atualizar'}
      </Button>
    </Box>
  );
}

const BASE_PATH = '/modulesHome/financeiro';
const MODULE_COLOR = '#FF8A00';
const MODULE_BG = '#FFE9D5';

interface SubMeta {
  desc: string;
  icon: React.ElementType;
  color: string;
}

const META: Record<string, SubMeta> = {
  '/modulesHome/financeiro/diretoria': {
    desc: 'Visão executiva: recebido × pago, evolução do caixa e resultado por unidade.',
    icon: ChartLineUp,
    color: '#1B88F5',
  },
  '/modulesHome/financeiro/dashboard': {
    desc: 'KPIs, aging, inadimplência e resultado num só lugar.',
    icon: Gauge,
    color: '#1B88F5',
  },
  '/modulesHome/financeiro/dre': {
    desc: 'Receita × despesa por categoria — clique e veja a composição.',
    icon: Scales,
    color: '#7C3AED',
  },
  '/modulesHome/financeiro/contas-receber': {
    desc: 'Recebíveis: status, vencimento, descrição completa e busca.',
    icon: CurrencyDollar,
    color: '#16A34A',
  },
  '/modulesHome/financeiro/inadimplencia': {
    desc: 'Honorários vencidos por cliente, faixa de atraso e ticket médio.',
    icon: WarningCircle,
    color: '#D97706',
  },
  '/modulesHome/financeiro/auditoria': {
    desc: 'Qualidade dos dados (inconsistências) + pontualidade de pagamento.',
    icon: MagnifyingGlass,
    color: '#2563EB',
  },
  '/modulesHome/financeiro/contas-pagar': {
    desc: 'Despesas a pagar: status, vencimento e centro de custo.',
    icon: Receipt,
    color: '#DC2626',
  },
  '/modulesHome/financeiro/custo-pessoal': {
    desc: 'Por categoria, área e colaborador (cruzando o Domínio) + % da receita.',
    icon: UsersThree,
    color: '#DB2777',
  },
  '/modulesHome/financeiro/fluxo': {
    desc: 'Recebido × pago por mês, saldo das contas e previsão.',
    icon: Bank,
    color: '#0EA5E9',
  },
  '/modulesHome/financeiro/gaps': {
    desc: 'Descasamento de caixa: quando o que vence a pagar supera o a receber.',
    icon: ArrowsLeftRight,
    color: '#F59E0B',
  },
  '/modulesHome/financeiro/franquias': {
    desc: 'Compare receita, despesa, resultado e margem entre as franquias.',
    icon: Buildings,
    color: '#0891B2',
  },
  '/modulesHome/financeiro/clientes': {
    desc: 'Ranking de clientes por faturamento e recorrência.',
    icon: Trophy,
    color: '#CA8A04',
  },
  '/modulesHome/financeiro/vendas': {
    desc: 'Vendas e itens do Conta Azul, com filtros.',
    icon: ShoppingBag,
    color: '#2563EB',
  },
  '/modulesHome/financeiro/contratos': {
    desc: 'Contratos e recorrência (MRR), próximos vencimentos.',
    icon: FileText,
    color: '#059669',
  },
};

const SECTIONS: { title: string; paths: string[] }[] = [
  {
    title: 'Visão Geral & Diretoria & Resultado',
    paths: [
      '/modulesHome/financeiro/dashboard',
      '/modulesHome/financeiro/diretoria',
      '/modulesHome/financeiro/dre',
    ],
  },
  {
    title: 'Recebíveis & Inadimplência & Auditoria',
    paths: [
      '/modulesHome/financeiro/contas-receber',
      '/modulesHome/financeiro/inadimplencia',
      '/modulesHome/financeiro/auditoria',
    ],
  },
  {
    title: 'Despesas & Pessoal',
    paths: ['/modulesHome/financeiro/contas-pagar', '/modulesHome/financeiro/custo-pessoal'],
  },
  {
    title: 'Caixa & Previsão',
    paths: ['/modulesHome/financeiro/fluxo', '/modulesHome/financeiro/gaps'],
  },
  { title: 'Franquias', paths: ['/modulesHome/financeiro/franquias'] },
  {
    title: 'Comercial',
    paths: [
      '/modulesHome/financeiro/clientes',
      '/modulesHome/financeiro/vendas',
      '/modulesHome/financeiro/contratos',
    ],
  },
];

interface Preview {
  value: string;
  label: string;
  tone?: 'pos' | 'neg' | 'warn';
}

const TONE: Record<string, string> = {
  pos: '#16A34A',
  neg: '#DC2626',
  warn: '#D97706',
};

// Preview do card a partir do /dashboard (1 chamada cobre os tiles financeiros).
function previewFor(
  path: string,
  d?: IFinanceiroDashboard,
  fluxo?: IFinanceiroFluxo,
  dreComp?: IFinanceiroDre,
): Preview | null {
  switch (path) {
    // Diretoria e Fluxo mostram "Saldo em contas" (snapshot de hoje, independe de
    // período) — igual ao KPI da própria tela. Antes o hub previa "resultado
    // faturado" (DRE/vencimento/mês), que divergia do regime de caixa das telas.
    case '/modulesHome/financeiro/diretoria':
    case '/modulesHome/financeiro/fluxo': {
      if (!fluxo) return null;
      // Mesma regra das telas: "Saldo em contas" exclui contas internas por
      // padrão (hideInternal default = true) — soma só as não-internas.
      const saldo = fluxo.saldo_contas
        .filter(c => !isContaInterna(c.tipo))
        .reduce((s, c) => s + c.saldo, 0);
      return {
        value: brl(saldo),
        label: 'saldo em contas',
        tone: saldo < 0 ? 'neg' : undefined,
      };
    }
    // DRE por competência (mesma base da tela DRE).
    case '/modulesHome/financeiro/dre': {
      if (!dreComp) return null;
      const res = dreComp.resultado;
      return {
        value: brl(res),
        label: `margem ${fmtPct(pct(res, dreComp.receita_total))}`,
        tone: res >= 0 ? 'pos' : 'neg',
      };
    }
    case '/modulesHome/financeiro/dashboard':
      if (!d) return null;
      return {
        value: brlCompact(d.receber.em_aberto.valor),
        label: `a receber · ${brlCompact(d.pagar.em_aberto.valor)} a pagar`,
      };
    case '/modulesHome/financeiro/contas-receber':
      if (!d) return null;
      return {
        value: brl(d.receber.em_aberto.valor),
        label: `${d.receber.em_aberto.qtd} títulos em aberto`,
      };
    case '/modulesHome/financeiro/contas-pagar':
      if (!d) return null;
      return {
        value: brl(d.pagar.em_aberto.valor),
        label: `${d.pagar.em_aberto.qtd} títulos em aberto`,
      };
    case '/modulesHome/financeiro/inadimplencia':
      if (!d) return null;
      return {
        value: brl(d.receber.vencido.valor),
        label: `${d.receber.vencido.qtd} títulos vencidos`,
        tone: d.receber.vencido.valor > 0 ? 'warn' : undefined,
      };
    default:
      return null;
  }
}

const CockpitCard = ({
  sub,
  index,
  preview,
}: {
  sub: IPrivateRoutes;
  index: number;
  preview: Preview | null;
}) => {
  const navigate = useNavigate();
  const { search } = useLocation();
  const meta = META[sub.path];
  const Icon = meta?.icon ?? CaretRight;
  const color = meta?.color ?? MODULE_COLOR;
  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: 0.03 * index }}
      whileHover={{ y: -5 }}
      style={{ height: '100%' }}
    >
      <GlassCard
        intensity="strong"
        hoverable
        p={4}
        cursor="pointer"
        h="full"
        position="relative"
        overflow="hidden"
        onClick={() => navigate({ pathname: sub.path, search })}
        role="button"
        transition="box-shadow 0.25s ease"
        _hover={{ boxShadow: `0 16px 38px -12px ${color}59` }}
      >
        <Box
          position="absolute"
          top="-38px"
          insetEnd="-38px"
          w="130px"
          h="130px"
          borderRadius="full"
          bg={`radial-gradient(circle, ${color}24, transparent 68%)`}
          pointerEvents="none"
        />
        <Flex direction="column" gap={3} h="full" position="relative">
          <Flex justify="space-between" align="center">
            <Flex
              align="center"
              justify="center"
              w="44px"
              h="44px"
              borderRadius="14px"
              bgGradient={`linear(to-br, ${color}, ${color}AF)`}
              color="white"
              boxShadow={`0 10px 22px -7px ${color}99`}
            >
              <Icon size={23} weight="fill" />
            </Flex>
            <Flex
              align="center"
              justify="center"
              w="26px"
              h="26px"
              borderRadius="full"
              bg={`${color}14`}
              color={color}
            >
              <CaretRight size={14} weight="bold" />
            </Flex>
          </Flex>

          <Heading as="h3" fontSize="15px" fontWeight={700} color="#0F172A">
            {sub.name}
          </Heading>

          <Box mt="auto" pt={3} borderTop="1px solid rgba(15,23,42,0.06)">
            {preview ? (
              <>
                <Text
                  fontSize="20px"
                  fontWeight={800}
                  color={preview.tone ? TONE[preview.tone] : '#0F172A'}
                  lineHeight="1.1"
                  className="tabular-nums"
                >
                  {preview.value}
                </Text>
                <Text fontSize="11px" color="#94A3B8" mt={0.5} noOfLines={1}>
                  {preview.label}
                </Text>
              </>
            ) : (
              <Text fontSize="13px" color="#64748B" lineHeight="1.45" noOfLines={2}>
                {meta?.desc ?? 'Acesse o submódulo'}
              </Text>
            )}
          </Box>
        </Flex>
      </GlassCard>
    </motion.div>
  );
};

export function ModulesHomeFinanceiroPage() {
  const submodules = usePermittedSubmodules(BASE_PATH);
  const porPath = new Map(submodules.map(s => [s.path, s]));
  const f = useFinanceiroFilters();
  const { data: dash } = useFinanceiroDashboard({ base: 'vencimento', ...f.apiParams });
  const { data: dashAnt } = useFinanceiroDashboard({ base: 'vencimento', ...f.prevApiParams });
  const { data: insights } = useFinanceiroInsights({ base: 'competencia', ...f.apiParams });
  // Fonte dos cards diretoria/fluxo (saldo em contas) e dre (competência) —
  // pra o preview do hub bater com o KPI da própria tela ao entrar.
  const { data: fluxoHub } = useFinanceiroFluxo({ base: 'competencia', ...f.apiParams });
  const { data: dreComp } = useFinanceiroDre({ base: 'competencia', ...f.apiParams });
  // Recebido/Pago em regime de CAIXA (baixas por data de pagamento) — dinheiro
  // que realmente entrou/saiu no período; bate com Entradas/Saídas do Fluxo.
  const { data: recCaixa } = useFinanceiroKpis({ tipo: 'RECEITA', base: 'caixa', ...f.apiParams });
  const { data: pagCaixa } = useFinanceiroKpis({ tipo: 'DESPESA', base: 'caixa', ...f.apiParams });
  const { data: recCaixaAnt } = useFinanceiroKpis({
    tipo: 'RECEITA',
    base: 'caixa',
    ...f.prevApiParams,
  });
  const { data: pagCaixaAnt } = useFinanceiroKpis({
    tipo: 'DESPESA',
    base: 'caixa',
    ...f.prevApiParams,
  });

  const usados = new Set<string>();
  const secoes = SECTIONS.map(sec => {
    const subs = sec.paths.map(p => porPath.get(p)).filter((s): s is IPrivateRoutes => Boolean(s));
    subs.forEach(s => usados.add(s.path));
    return { title: sec.title, subs };
  }).filter(sec => sec.subs.length > 0);

  const outros = submodules.filter(s => !usados.has(s.path));
  if (outros.length > 0) secoes.push({ title: 'Outros', subs: outros });

  let cardIndex = 0;

  return (
    <GlassPageBg>
      <Box maxW="1280px" mx="auto" w="full" px={{ base: 4, md: 8 }} py={{ base: 6, md: 10 }}>
        <Flex align="center" gap={4} mb={7}>
          <Flex align="center" justify="center" w="60px" h="60px" borderRadius="2xl" bg={MODULE_BG}>
            <CurrencyDollar size={30} color={MODULE_COLOR} weight="duotone" />
          </Flex>
          <Box>
            <Heading
              fontSize={{ base: '24px', md: '32px' }}
              fontWeight={800}
              color="#0F172A"
              letterSpacing="-0.02em"
            >
              Financeiro
            </Heading>
            <Text fontSize="sm" color="#64748B">
              Cockpit do escritório — Conta Azul, todas as franquias
            </Text>
          </Box>
        </Flex>

        {submodules.length === 0 ? (
          <GlassCard intensity="medium" p={10} textAlign="center">
            <Text color="#64748B">Nenhum submódulo disponível para o seu perfil.</Text>
          </GlassCard>
        ) : (
          <>
            {/* IA · Insights do mês (ao lado da visão geral) */}
            {insights && insights.achados.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4 }}
              >
                <GlassCard intensity="strong" p={5} mb={6} position="relative" overflow="hidden">
                  <Box
                    position="absolute"
                    top="-40px"
                    insetEnd="-40px"
                    w="160px"
                    h="160px"
                    borderRadius="full"
                    bg="radial-gradient(circle, rgba(124,58,237,0.18), transparent 68%)"
                    pointerEvents="none"
                  />
                  <Flex align="center" gap={2.5} mb={3} position="relative">
                    <Flex
                      align="center"
                      justify="center"
                      w="36px"
                      h="36px"
                      borderRadius="12px"
                      bgGradient="linear(to-br, #7C3AED, #7C3AEDB0)"
                      color="white"
                      flexShrink={0}
                    >
                      <Sparkle size={19} weight="fill" />
                    </Flex>
                    <Box minW={0}>
                      <Text
                        fontSize="11px"
                        fontWeight={800}
                        color="#7C3AED"
                        textTransform="uppercase"
                        letterSpacing="0.1em"
                      >
                        Insights · resumo do mês
                      </Text>
                      <Text fontSize="sm" color="#0F172A" fontWeight={500} noOfLines={2}>
                        {insights.resumo}
                      </Text>
                    </Box>
                    <InsightsAtualizar
                      params={{ base: 'competencia', ...f.apiParams }}
                      geradoEm={insights.gerado_em}
                      liberadaEm={insights.atualizacao_liberada_em}
                    />
                  </Flex>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3} position="relative">
                    {insights.achados.map(a => (
                      <Flex
                        key={a.chave}
                        align="start"
                        gap={2.5}
                        p={3}
                        borderRadius="lg"
                        bg="rgba(255,255,255,0.5)"
                      >
                        <Box
                          w="8px"
                          h="8px"
                          mt="6px"
                          borderRadius="full"
                          bg={SEV[a.severidade] ?? '#94A3B8'}
                          flexShrink={0}
                        />
                        <Box minW={0}>
                          <Text fontSize="sm" fontWeight={700} color="#0F172A">
                            {a.titulo}
                          </Text>
                          {a.detalhe && (
                            <Text fontSize="xs" color="#64748B">
                              {a.detalhe}
                            </Text>
                          )}
                        </Box>
                      </Flex>
                    ))}
                  </SimpleGrid>
                </GlassCard>
              </motion.div>
            )}

            {/* Tira-resumo: os 6 números que o dono bate o olho */}
            <SimpleGrid columns={{ base: 2, md: 3, xl: 6 }} spacing={4} mb={8}>
              <KpiCard
                label="A Receber"
                valor={dash?.receber.em_aberto.valor ?? 0}
                qtd={dash?.receber.em_aberto.qtd}
                color="#16A34A"
                icon={<TrendUp size={20} weight="duotone" />}
                index={0}
                delta={mkDelta(
                  dash?.receber.em_aberto.valor ?? 0,
                  dashAnt?.receber.em_aberto.valor ?? 0,
                  true,
                )}
                info={
                  <InfoPopover title="A Receber">
                    Saldo em aberto a receber no mês (não pago, não vencido). Base: vencimento.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Recebido"
                valor={recCaixa?.pago.valor ?? 0}
                qtd={recCaixa?.pago.qtd}
                color="#059669"
                icon={<Coins size={20} weight="duotone" />}
                index={1}
                delta={mkDelta(recCaixa?.pago.valor ?? 0, recCaixaAnt?.pago.valor ?? 0, true)}
                info={
                  <InfoPopover title="Recebido">
                    Dinheiro que <b>entrou</b> no período (baixas por data de pagamento — regime de
                    caixa). Bate com as Entradas do Fluxo/Diretoria no mesmo período.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="A Pagar"
                valor={dash?.pagar.em_aberto.valor ?? 0}
                qtd={dash?.pagar.em_aberto.qtd}
                color="#DC2626"
                icon={<TrendDown size={20} weight="duotone" />}
                index={1}
                delta={mkDelta(
                  dash?.pagar.em_aberto.valor ?? 0,
                  dashAnt?.pagar.em_aberto.valor ?? 0,
                  false,
                )}
                info={
                  <InfoPopover title="A Pagar">
                    Saldo em aberto a pagar no mês (despesas não pagas, não vencidas).
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Pago"
                valor={pagCaixa?.pago.valor ?? 0}
                qtd={pagCaixa?.pago.qtd}
                color="#B91C1C"
                icon={<Receipt size={20} weight="duotone" />}
                index={3}
                delta={mkDelta(pagCaixa?.pago.valor ?? 0, pagCaixaAnt?.pago.valor ?? 0, false)}
                info={
                  <InfoPopover title="Pago">
                    Dinheiro que <b>saiu</b> no período (despesas baixadas por data de pagamento —
                    regime de caixa). Bate com as Saídas do Fluxo/Diretoria no mesmo período.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Saldo Previsto"
                valor={dash?.saldo_previsto ?? 0}
                color="#1B88F5"
                icon={<Scales size={20} weight="duotone" />}
                index={4}
                info={
                  <InfoPopover title="Saldo Previsto">
                    A receber − A pagar em aberto: o que deve sobrar (ou faltar) no mês.
                  </InfoPopover>
                }
              />
              <KpiCard
                label="Inadimplência"
                valor={dash?.receber.vencido.valor ?? 0}
                qtd={dash?.receber.vencido.qtd}
                color="#D97706"
                icon={<WarningCircle size={20} weight="duotone" />}
                index={5}
                delta={mkDelta(
                  dash?.receber.vencido.valor ?? 0,
                  dashAnt?.receber.vencido.valor ?? 0,
                  false,
                )}
                info={
                  <InfoPopover title="Inadimplência">
                    Honorários vencidos <b>até o fim do período</b> e ainda não pagos (o atrasado
                    antigo não some — só o fim do período corta).
                  </InfoPopover>
                }
              />
            </SimpleGrid>

            {/* Grade contínua: rótulos full-width, cards fluem sem deixar coluna vazia */}
            {/* Cada seção é um bloco: título numa linha, os cards logo abaixo. */}
            {secoes.map((sec, si) => {
              const secColor = META[sec.subs[0].path]?.color ?? MODULE_COLOR;
              return (
                <Box key={sec.title} mt={si === 0 ? 0 : 6}>
                  <Flex align="center" gap={2.5} mb={3}>
                    <Box w="7px" h="7px" borderRadius="full" bg={secColor} flexShrink={0} />
                    <Text
                      fontSize="11px"
                      fontWeight={800}
                      color="#475569"
                      textTransform="uppercase"
                      letterSpacing="0.1em"
                      whiteSpace="nowrap"
                    >
                      {sec.title}
                    </Text>
                    <Box flex="1" h="1px" bg="rgba(15,23,42,0.06)" />
                  </Flex>
                  <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
                    {sec.subs.map(sub => {
                      cardIndex += 1;
                      return (
                        <CockpitCard
                          key={sub.path}
                          sub={sub}
                          index={cardIndex}
                          preview={previewFor(sub.path, dash, fluxoHub, dreComp)}
                        />
                      );
                    })}
                  </SimpleGrid>
                </Box>
              );
            })}
          </>
        )}
      </Box>
    </GlassPageBg>
  );
}
